import React, { useState, useEffect } from 'react';
import {
    Grid, Paper, CircularProgress, Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
    TableFooter, TablePagination, Select, MenuItem, FormControl, Typography, Card, Button, Fab, TextField,
    useMediaQuery, FormHelperText, Container, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    ListSubheader, Checkbox, ListItemText, Tooltip, Divider
} from '@mui/material';
import { Add, Close, Edit, Delete } from '@mui/icons-material';
import { userList } from '../../services/UserService';
import { regList } from '../../services/RegistrationService';
import { getGroups, addGroup, editGroup, delGroup } from '../../services/GroupService';
import { useTheme } from '@mui/material/styles';
import { trClose, TPA, FormatDT, pBtn, nBtn, adminTH, PerChk, SnackAlert } from '../../common/Common';

const defGroupObj = {
    _id: '',
    name: '',
    description: '',
    members: []
};

export default function GroupManagement() {
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const userId = sessionStorage.getItem('userId');
    const [loading, setloading] = useState(false);
    const [groupObj, setgroupObj] = useState(defGroupObj);
    const [groupList, setgroupList] = useState([]);
    const [users, setusers] = useState([]);
    const [memberList, setmemberList] = useState([]);
    const [reassignList, setreassignList] = useState(new Map());
    const [dialogMode, setdialogMode] = useState('');
    const [alertMessage, setalertMessage] = useState('');
    const [alertStatus, setalertStatus] = useState('');
    const [search, setsearch] = useState('');
    const [page, setpage] = useState(0);
    const aeValidity = loading || !groupObj?.name || !groupObj?.description || dialogMode === 'Add' && groupObj?.members?.length <= 0;
    const userIdToName = (id) => (users?.find(u => u._id === id)?.full_name || id);
    const userIdToDetails = (id) => { const u = memberList?.find(u => u._id === id); return u ? `${u.full_name || ''} | ${u.mobile_number || ''} | ${u.support_via || ''} | ${u.notes || ''}` : id };
    const groupListF = groupList.filter((g) => {
        return (
            g?.name?.toLowerCase().includes(search) ||
            g?.description?.toLowerCase().includes(search) ||
            g?.members?.some(m => userIdToName(m.userId)?.toLowerCase()?.includes(search))
        )
    });
    const groupListP = groupListF.slice(page * 10, page * 10 + 10);

    useEffect(() => { groupFetch(); }, [])

    const groupFetch = async () => {
        setloading(true);
        try {
            const resp1 = await getGroups();
            const resp2 = await userList();
            const resp3 = await regList();
            if (resp1?.state === 'SUCCESS') { setgroupList(resp1?.result?.sort((a, b) => (a.name || "").localeCompare(b.name || ""))); }
            if (resp2?.state === 'SUCCESS' && resp3?.state === 'SUCCESS') {
                setusers(resp2?.result);
                const usr = resp2?.result?.filter(user => user.status === 'Active' && user.roletype_key === 'SAS_MEMBER');
                const reg = resp3?.result;
                const merged = usr.map(user => {
                    const register = reg.find(item => item.user_id === user._id);
                    return { ...(register || {}), ...(user || {}) };
                });
                setmemberList(merged);
            }
        }
        catch (e) { console.log(e); }
        setloading(false);
    }

    const groupAdd = async () => {
        setloading(true);
        try {
            const resp = await addGroup({
                name: groupObj?.name,
                description: groupObj?.description,
                members: groupObj?.members,
                createdBy: userId
            });
            if (resp?.state === 'SUCCESS') {
                setdialogMode(''); groupFetch();
                setalertMessage('Group created successfully'); setalertStatus('success');
            }
        }
        catch (e) {
            console.log(e);
            setalertMessage(`A group named ${groupObj?.name} already exists - Try a different name`); setalertStatus('error');
        }
        setloading(false);
    }

    const groupEdit = async () => {
        setloading(true);
        try {
            const resp = await editGroup(groupObj?._id, {
                name: groupObj?.name,
                description: groupObj?.description,
                members: groupObj?.members,
                updatedBy: userId
            });
            if (resp?.state === 'SUCCESS') {
                setdialogMode(''); groupFetch();
                setalertMessage('Group updated successfully'); setalertStatus('success');
            }
        }
        catch (e) {
            console.log(e);
            setalertMessage(`A group named ${groupObj?.name} already exists - Try a different name`); setalertStatus('error');
        }
        setloading(false);
    }

    const groupDelete = async () => {
        setloading(true);
        try {
            const resp = await delGroup(groupObj?._id);
            if (resp?.state === 'SUCCESS') {
                setdialogMode(''); groupFetch();
                setalertMessage('Group deleted successfully'); setalertStatus('success');
            }
        }
        catch (e) {
            console.log(e);
            setalertMessage('A group cannot be deleted if it still has members'); setalertStatus('error');
        }
        setloading(false);
    }

    const reassignCheck = () => {
        const reassignments = new Map();
        groupList.filter(g => g._id !== groupObj._id).forEach(g => {
            g.members.forEach(m => {
                if (groupObj.members.map(m => m.userId).includes(m.userId)) { reassignments.set(m.userId, g.name) }
            });
        });
        if (reassignments.size > 0) { setreassignList(reassignments) }
        else { dialogMode === 'Add' ? groupAdd() : groupEdit() }
    }

    return (<>
        <Fab title='Create new group' onClick={() => { setgroupObj(defGroupObj); setdialogMode('Add'); }} sx={{ position: 'fixed', bottom: 40, right: 20 }}><Add sx={{ color: '#fff' }} /></Fab>

        <SnackAlert message={alertMessage} status={alertStatus} hide={() => setalertMessage('')} />

        <Container><Grid container>
            <Grid item xs={12} sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'flex-end', alignItems: isMobile ? 'flex-end' : null, gap: '10px', mb: 2 }}>
                <FormControl sx={{ width: '130px' }}><TextField value={search} size='small' onChange={(e) => { setsearch(e.target.value?.toLowerCase()); setpage(0); }} label='Search' /></FormControl>
            </Grid>

            <Grid item xs={12}>{groupListF?.length > 0 ?
                <Paper sx={{ width: '100%', overflow: 'hidden' }}><TableContainer sx={{ maxHeight: 440 }}><Table>
                    <TableHead sx={adminTH}><TableRow>
                        <TableCell sx={{ color: '#fff' }} width='15%'>Name</TableCell>
                        <TableCell sx={{ color: '#fff' }} width='40%'>Description</TableCell>
                        <TableCell sx={{ color: '#fff' }} width='40%'>Members</TableCell>
                        <TableCell width='5%' />
                    </TableRow></TableHead>

                    <TableBody>{groupListP.map((g) => (<TableRow key={g?._id} sx={{ '&:hover': { cursor: 'pointer', bgcolor: '#ddd' } }} onClick={() => { setgroupObj(g); setdialogMode('View'); }}>
                        <TableCell width='15%'>{g?.name}</TableCell>
                        <TableCell width='40%'>{g?.description}</TableCell>
                        <TableCell width='40%'>({g?.members.length}) {g?.members?.map(m => userIdToName(m.userId))?.join(', ')}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()} align='right' width='5%'>
                            <IconButton title='Edit group' onClick={() => { setgroupObj(g); setdialogMode('Edit'); }} sx={{ color: '#b5651d' }}><Edit /></IconButton>
                            <Tooltip title={g?.members?.length > 0 && 'Group cannot be deleted if it has members'}><span><IconButton title='Delete group' onClick={() => { setgroupObj(g); setdialogMode('Delete'); }} sx={{ color: '#c00' }} disabled={g?.members?.length > 0}><Delete /></IconButton></span></Tooltip>
                        </TableCell>
                    </TableRow>))}</TableBody>

                    <TableFooter><TableRow>
                        <TablePagination count={groupListF.length} page={page} rowsPerPage={10} sx={{ '.MuiTablePagination-displayedRows': { display: 'none' } }}
                            onPageChange={(e, p) => setpage(p)} rowsPerPageOptions={[]} ActionsComponent={TPA} />
                    </TableRow></TableFooter>
                </Table></TableContainer></Paper>
                :
                <Typography variant='h6' sx={{ textAlign: 'center' }}>
                    {search ? 'No results for your search' : 'No records'}
                </Typography>
            }</Grid>
        </Grid></Container>

        <Dialog fullWidth open={dialogMode === 'Add' || dialogMode === 'Edit'}>
            <Close sx={trClose} onClick={() => setdialogMode('')} disabled={loading} />

            <DialogTitle sx={{ mt: '10px', mb: '5px', borderBottom: '1px solid #b5651d' }}>
                <Typography variant='h6'>
                    {dialogMode === 'Add' ? 'Create new group' : 'Edit group'}
                </Typography>
            </DialogTitle>

            <DialogContent><Card sx={{ gap: '10px' }}>
                <TextField required label="Name" value={groupObj.name} inputProps={{ maxLength: 32 }}
                    onChange={(e) => { if (/^[a-zA-Z0-9-_ ]*$/.test(e.target.value)) { setgroupObj({ ...groupObj, name: e.target.value?.trimStart() }); } }} />

                <TextField required multiline rows={4} label="Description" value={groupObj.description}
                    helperText={`${groupObj?.description?.trim() ? groupObj.description.trim().split(/\s+/).length : 0}/200`}
                    onChange={(e) => {
                        const words = e.target.value.trim() ? e.target.value.trim().split(/\s+/) : [];
                        if (words.length <= 200) { setgroupObj({ ...groupObj, description: e.target.value }); }
                    }} />

                <FormControl fullWidth>
                    <FormHelperText>Members</FormHelperText>
                    <Select multiple value={groupObj?.members?.map(m => m.userId) ?? []}
                        onChange={(e) => setgroupObj({ ...groupObj, members: e.target.value?.map(id => ({ userId: id, assignedBy: userId })) })}
                        renderValue={(selected) => { return `${selected.length} members: ` }}>
                        {[groupList.find(g => g._id === groupObj._id)].filter(Boolean).flatMap(g => [
                            <ListSubheader key={`${g._id}-header`}><b>{g?.name}</b> created by <b>{userIdToName(g?.createdBy)}</b> (Current group)</ListSubheader>,
                            ...g?.members?.sort((a, b) => (userIdToName(a.userId) || "").localeCompare(userIdToName(b.userId) || ""))?.map(m => (
                                <MenuItem key={m.userId} value={m.userId} sx={{ ml: 2, my: -2 }}>
                                    <Checkbox size="small" checked={groupObj?.members?.some(u => u.userId === m.userId)} />
                                    <ListItemText>{userIdToDetails(m.userId)}</ListItemText>
                                </MenuItem>
                            )),
                            <Divider variant='middle' />
                        ])}

                        {groupList.filter(g => g._id !== groupObj._id && g?.members?.length > 0).flatMap(g => [
                            <ListSubheader key={`${g._id}-header`}><b>{g?.name}</b> created by <b>{userIdToName(g?.createdBy)}</b></ListSubheader>,
                            ...g?.members?.sort((a, b) => (userIdToName(a.userId) || "").localeCompare(userIdToName(b.userId) || ""))?.map(m => (
                                <MenuItem key={m.userId} value={m.userId} sx={{ ml: 2, my: -2 }}>
                                    <Checkbox size="small" checked={groupObj?.members?.some(u => u.userId === m.userId)} />
                                    <ListItemText>{userIdToDetails(m.userId)}</ListItemText>
                                </MenuItem>
                            )),
                            <Divider variant='middle' />
                        ])}

                        <ListSubheader>Unassigned</ListSubheader>
                        {memberList.filter(m => {
                            const memberIds = new Set(groupList?.flatMap(g => g.members.map(m => m.userId)) ?? []);
                            return !memberIds.has(m._id)
                        })?.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))?.
                            map(m => (<MenuItem key={m._id} value={m._id} sx={{ ml: 2, my: -2 }}>
                                <Checkbox size="small" checked={groupObj?.members?.some(u => u.userId === m._id)} />
                                <ListItemText>{m.full_name} | {m.mobile_number} | {m.support_via} | {m.notes}</ListItemText>
                            </MenuItem>))}
                    </Select>
                    {groupObj?.members?.length > 0 && <Paper sx={{ width: '100%', overflow: 'hidden' }}><TableContainer sx={{ maxHeight: 220 }}><Table>
                        <TableBody>{groupObj?.members?.sort((a, b) => (userIdToName(a.userId).localeCompare(userIdToName(b.userId))))?.map(m =>
                            <TableRow key={m.userId}><TableCell>{userIdToDetails(m.userId)}</TableCell></TableRow>
                        )}</TableBody>
                    </Table></TableContainer></Paper>}
                </FormControl>
            </Card></DialogContent>

            <DialogActions sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', mt: 4 }}>
                <Button disabled={aeValidity} sx={pBtn} onClick={reassignCheck}>
                    {loading ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={dialogMode === 'Delete'}>
            <Close sx={trClose} onClick={() => setdialogMode('')} disabled={loading} />
            <DialogTitle sx={{ mb: '5px', borderBottom: '1px solid #b5651d' }}><Typography variant='h6'>Confirmation</Typography></DialogTitle>
            <DialogContent><Typography sx={{ mb: '20px' }}>Are you sure you want to delete <b>{groupObj?.name}</b> group?</Typography></DialogContent>
            <DialogActions style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                <Button sx={nBtn} onClick={groupDelete} disabled={loading}>Delete</Button>
            </DialogActions>
        </Dialog>

        <Dialog fullWidth open={dialogMode === 'View'}>
            <Close sx={trClose} onClick={() => setdialogMode('')} disabled={loading} />

            <DialogTitle sx={{ mt: '10px', mb: '5px', borderBottom: '1px solid #b5651d' }}>
                <Typography variant='h6'>{groupObj?.name}</Typography>
                <Typography sx={{ color: 'GrayText' }}>Created by <b>{userIdToName(groupObj?.createdBy)}</b> on <b>{FormatDT(groupObj?.created_at)}</b></Typography>
            </DialogTitle>

            <DialogContent><Card sx={{ gap: '10px' }}>
                <Typography><b>Description:</b> {groupObj.description}</Typography>

                {groupObj?.members?.length > 0 ?
                    <>
                        <Typography><b>{groupObj?.members?.length} members:</b></Typography>
                        <Paper sx={{ width: '100%', overflow: 'hidden' }}><TableContainer sx={{ maxHeight: 220 }}><Table>
                            <TableBody>{groupObj?.members?.sort((a, b) => (userIdToName(a.userId).localeCompare(userIdToName(b.userId))))?.map(m =>
                                <TableRow key={m.userId}><TableCell>{userIdToDetails(m.userId)}</TableCell></TableRow>
                            )}</TableBody>
                        </Table></TableContainer></Paper>
                    </>
                    :
                    <Typography>No members in this group</Typography>
                }
            </Card></DialogContent>
        </Dialog>

        <Dialog open={reassignList.size > 0}>
            <Close sx={trClose} onClick={() => setreassignList(new Map())} disabled={loading} />
            <DialogTitle sx={{ mb: '5px', borderBottom: '1px solid #b5651d' }}>Confirm Member Reassignment</DialogTitle>
            <DialogContent>
                <Typography>The following members are already assigned to other groups:</Typography>
                <ul>{[...reassignList.entries()].map(([userId, groupName]) => (
                    <li key={userId}><b>{userIdToName(userId)}</b> currently in <b>{groupName}</b></li>
                ))}</ul>
                <Typography>Do you want to reassign them to this group?</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { dialogMode === 'Add' ? groupAdd() : groupEdit(); setreassignList(new Map()); }} disabled={loading}>Yes</Button>
            </DialogActions>
        </Dialog>
    </>)
}